import { Injectable, Injector, OnDestroy, Optional } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IBreadcrumb, IMenu } from './layout.type';

interface IOptions {
  breadFn?(items: IBreadcrumb[]): IBreadcrumb[];
}

const uniqueElementsBy = (breads: IBreadcrumb[], fn: (bread1: IBreadcrumb, bread2: IBreadcrumb) => boolean) =>
  breads.reduce((result: IBreadcrumb[], v: IBreadcrumb) => {
    if (!result.some((x: IBreadcrumb) => fn(v, x))) {
      // 判断是否重复
      result.push(v);
    }
    return result;
  }, []);

@Injectable({ providedIn: 'root' })
export class MLayoutService implements OnDestroy {
  headerVisible$ = new BehaviorSubject<boolean>(true);
  breadcrumb$ = new BehaviorSubject<IBreadcrumb[]>([]);
  sideMenuClick$ = new BehaviorSubject<IMenu[]>([]);
  sideMenuVisible$ = new BehaviorSubject<boolean>(true);
  menuVisible$ = new BehaviorSubject<boolean>(true);

  // 这里记录路由切换时，组件加载时，传入的参数
  options$ = new BehaviorSubject<IOptions>({});
  routerChange = new Subscription();

  constructor(private injector: Injector, private router: Router, @Optional() private i18n: TranslateService) {
    this.routerChange = this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      this.updateBreads();
    });
  }

  private updateBreads(): void {
    let route = this.injector.get(ActivatedRoute);
    let titles: IBreadcrumb[] = [];
    while (route.firstChild) {
      route = route.firstChild;
      const data = (route.snapshot && route.snapshot.data) || {};
      if (data.breadcrumbs) {
        if (typeof data.breadcrumbs === 'function') {
          const breadcrumbs = data.breadcrumbs(route);
          titles = [...titles, ...breadcrumbs];
        } else if (Array.isArray(data.breadcrumbs)) {
          titles = [...titles, ...data.breadcrumbs];
        } else {
          console.warn('route breadcrumbs 只支持 Array 和 function, 请查看路由配置');
        }

        titles = titles.map((bread: IBreadcrumb) => {
          if (bread.titleI18n) {
            return {
              name: this.i18n.instant(bread.titleI18n),
              link: bread.link || bread.path
            };
          }
          return bread;
        });
      } else if (data.title || data.titleI18n) {
        if (this.i18n) {
          titles.push({
            name: data.title ? data.title : this.i18n.instant(data.titleI18n),
            link: data.path
          });
        } else {
          titles.push({
            name: data.title,
            link: data.path
          });
        }
      }
    }
    this.breadcrumb$.next(
      uniqueElementsBy(this.formatBreakcrumb(titles), (bread1: IBreadcrumb, bread2: IBreadcrumb) => {
        return bread1.name === bread2.name && bread1.path === bread2.path && bread1.link === bread2.link;
      })
    );
    this.options$.next({});
  }

  private formatBreakcrumb(breads: IBreadcrumb[]): IBreadcrumb[] {
    const options = this.options$.value;
    if (typeof options.breadFn === 'function') {
      return options.breadFn(breads);
    }
    return breads;
  }

  toggleHeader(show?: boolean): void {
    if (show === undefined) {
      this.headerVisible$.next(!this.headerVisible$.value);
    } else {
      this.headerVisible$.next(!!show);
    }
  }

  toggleSideMenu(show?: boolean): void {
    if (show === undefined) {
      this.sideMenuVisible$.next(!this.sideMenuVisible$.value);
    } else {
      this.sideMenuVisible$.next(!!show);
    }
  }

  toggleMenu(show?: boolean): void {
    if (show === undefined) {
      this.menuVisible$.next(!this.menuVisible$.value);
    } else {
      this.menuVisible$.next(!!show);
    }
  }

  ngOnDestroy(): void {
    this.routerChange.unsubscribe();
  }
}
